import { cn } from "@hey404/ui";
import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronRight,
  MessageCircle,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { apiFetch } from "./lib/auth";
import { btnPrimary, inputClass } from "./admin-ui";
import { useConfirm } from "./confirm-dialog";
import { RowMenu } from "./row-menu";
import {
  ChatComposer,
  CommentBubble,
  CommunityHeader,
  CommunityTag,
  Composer,
  PostAuthor,
  PostBody,
  ShowAllButton,
} from "./community-ui";

// Threads (and replies inside a thread) shown before "Ver todos".
const FORUM_PREVIEW = 4;
import { timeAgo } from "./lib/time";

type ForumPost = {
  id: string;
  userId: string;
  userName: string;
  userAvatarUrl: string | null;
  title: string | null;
  body: string;
  approved: boolean;
  createdAt: string;
};
type Thread = ForumPost & { replyCount: number };
type ForumMeta = { title: string; description: string };

const PendingBadge = () => (
  <CommunityTag tone="bg-lemon">Pendiente</CommunityTag>
);

/**
 * Course-level forum (UNB2-118). Self-contained: fetches the forum config +
 * threads for `courseId`, lets the student open a thread, start one, and reply.
 * Moderators (canModerate, from the API) also see pending posts — dimmed, with
 * inline approve/reject — and can delete any live post (with a confirm). A 403
 * means the forum is disabled or the user has no access.
 */
export function CourseForum({ courseId }: { courseId: string }) {
  const [status, setStatus] = useState<
    "loading" | "ready" | "disabled" | "error"
  >("loading");
  const [meta, setMeta] = useState<ForumMeta | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [canModerate, setCanModerate] = useState(false);
  const [open, setOpen] = useState<{
    thread: ForumPost;
    replies: ForumPost[];
  } | null>(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  // The thread form starts folded behind "Abrir un hilo" so the list leads.
  const [composing, setComposing] = useState(false);
  const [allThreads, setAllThreads] = useState(false);
  const [allReplies, setAllReplies] = useState(false);
  const [askConfirm, confirmDialog] = useConfirm();

  const loadForum = async () => {
    try {
      const data = await apiFetch<{
        forum: ForumMeta;
        canModerate: boolean;
        threads: Thread[];
      }>(`/api/community/courses/${courseId}/forum`);
      setMeta(data.forum);
      setThreads(data.threads);
      setCanModerate(data.canModerate);
      setStatus("ready");
    } catch (cause) {
      setStatus(
        cause instanceof Error && cause.message === "forbidden"
          ? "disabled"
          : "error",
      );
    }
  };
  useEffect(() => {
    void loadForum();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on course change
  }, [courseId]);

  async function openThread(id: string) {
    setOpenLoading(true);
    try {
      const data = await apiFetch<{
        thread: ForumPost;
        replies: ForumPost[];
        canModerate: boolean;
      }>(`/api/community/courses/${courseId}/threads/${id}`);
      setCanModerate(data.canModerate);
      setOpen((current) => {
        if (current?.thread.id !== data.thread.id) setAllReplies(false);
        return { thread: data.thread, replies: data.replies };
      });
    } finally {
      setOpenLoading(false);
    }
  }

  async function createThread(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ thread: { approved: boolean } }>(
        `/api/community/courses/${courseId}/threads`,
        {
          method: "POST",
          body: JSON.stringify({ title: title.trim(), body: body.trim() }),
        },
      );
      setTitle("");
      setBody("");
      setComposing(false);
      setNotice(
        res.thread.approved
          ? ""
          : "Tu hilo quedó pendiente de aprobación de un moderador.",
      );
      await loadForum();
    } finally {
      setBusy(false);
    }
  }

  async function sendReply(event: FormEvent) {
    event.preventDefault();
    if (!open || !reply.trim() || busy) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ reply: { approved: boolean } }>(
        `/api/community/courses/${courseId}/threads/${open.thread.id}/replies`,
        { method: "POST", body: JSON.stringify({ body: reply.trim() }) },
      );
      setReply("");
      setNotice(
        res.reply.approved ? "" : "Tu respuesta quedó pendiente de aprobación.",
      );
      await openThread(open.thread.id);
      void loadForum();
    } finally {
      setBusy(false);
    }
  }

  // Reload the list and, if a thread is open, that thread — dropping back to the
  // list when it no longer exists (rejected or deleted).
  async function refresh() {
    await loadForum();
    if (open) {
      try {
        await openThread(open.thread.id);
      } catch {
        setOpen(null);
      }
    }
  }
  // Approve (approved:true) or reject (hidden:true) via the admin moderation
  // endpoint — the inline alternative to the Moderación queue.
  async function moderate(id: string, approve: boolean) {
    await apiFetch(`/api/admin/moderation/forum/${id}`, {
      method: "PATCH",
      body: JSON.stringify(approve ? { approved: true } : { hidden: true }),
    });
    await refresh();
  }
  async function remove(id: string) {
    await apiFetch(`/api/community/forum-posts/${id}`, { method: "DELETE" });
    await refresh();
  }

  // Moderator controls for one post: approve/reject if pending, else delete
  // (delete asks for confirmation first).
  const confirmRemove = async (post: ForumPost) => {
    const sure = await askConfirm({
      title: post.title
        ? `¿Eliminar el hilo «${post.title}»?`
        : "¿Eliminar esta respuesta?",
      message: post.title
        ? "Se borra con todas sus respuestas y deja de verse en el foro. Esta acción no se puede deshacer."
        : "Deja de verse en el foro. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (sure) void remove(post.id);
  };

  if (status === "loading")
    return (
      <p className="text-ink-soft font-mono text-sm" role="status">
        Cargando el foro…
      </p>
    );
  if (status === "disabled")
    return (
      <p className="text-ink-soft text-sm">
        El foro no está disponible para este curso.
      </p>
    );
  if (status === "error")
    return (
      <p className="text-ink-soft text-sm">
        No pudimos cargar el foro. Prueba de nuevo en un momento.
      </p>
    );

  // --- Thread detail -------------------------------------------------------
  if (open) {
    const moderation = (item: ForumPost, isThread = false) =>
      canModerate ? (
        <RowMenu
          ghost={!isThread}
          label={
            isThread
              ? "Moderar el hilo"
              : `Moderar la respuesta de ${item.userName}`
          }
          items={
            item.approved
              ? [
                  {
                    label: isThread ? "Eliminar hilo" : "Eliminar respuesta",
                    icon: Trash2,
                    danger: true,
                    onSelect: () => confirmRemove(item),
                  },
                ]
              : [
                  {
                    label: "Aprobar",
                    icon: Check,
                    onSelect: () => moderate(item.id, true),
                  },
                  {
                    label: "Rechazar",
                    icon: X,
                    danger: true,
                    onSelect: () => moderate(item.id, false),
                  },
                ]
          }
        />
      ) : null;
    const thread = open.thread;
    const count = open.replies.length;
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(null)}
          className="border-ink bg-paper border-chip rounded-control hover:bg-paper-dim inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 text-sm font-bold transition-colors"
        >
          <ArrowLeft size={15} aria-hidden="true" /> Volver al foro
        </button>
        {notice && (
          <p
            role="status"
            className="border-ink bg-lemon border-chip rounded-control mt-4 p-3 text-sm"
          >
            {notice}
          </p>
        )}
        {/* The opening post as a card on its own: tilted "Hilo" sticker, the
            author, a display title and the text, with the reply count in a
            footer strip. Replies follow as conversation bubbles. */}
        <article
          className={cn(
            "border-ink bg-paper border-surface shadow-brutal relative mt-5 p-5 sm:p-6",
            !thread.approved && "opacity-70",
          )}
        >
          <span className="border-ink bg-lemon border-chip shadow-brutal absolute -top-3 left-5 rotate-[-3deg] px-2 py-0.5 font-mono text-xs font-bold uppercase">
            Hilo
          </span>
          <div className="flex items-start justify-between gap-3">
            <PostAuthor
              name={thread.userName}
              avatarUrl={thread.userAvatarUrl}
              createdAt={thread.createdAt}
              tone="bg-grape"
            >
              {!thread.approved && <PendingBadge />}
            </PostAuthor>
            {moderation(thread, true)}
          </div>
          {thread.title && (
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight">
              {thread.title}
            </h3>
          )}
          <PostBody text={thread.body} className="mt-2 [&_p]:text-[15px]" />
          <div className="border-ink/20 text-ink-soft mt-4 flex items-center gap-2 border-t border-dashed pt-3 text-sm">
            <MessageCircle size={15} aria-hidden="true" />
            {count === 0
              ? "Sin respuestas todavía"
              : `${count} ${count === 1 ? "respuesta" : "respuestas"}`}
          </div>
        </article>

        <div className="mt-5 space-y-3.5">
          {(allReplies
            ? open.replies
            : open.replies.slice(0, FORUM_PREVIEW)
          ).map((item) => (
            <div
              key={item.id}
              className={item.approved ? undefined : "opacity-60"}
            >
              <CommentBubble
                name={item.userName}
                avatarUrl={item.userAvatarUrl}
                createdAt={item.createdAt}
                body={item.body}
                tone="bg-paper"
                tags={!item.approved && <PendingBadge />}
                menu={moderation(item)}
              />
            </div>
          ))}
        </div>
        {count > FORUM_PREVIEW && (
          <ShowAllButton
            expanded={allReplies}
            hidden={count - FORUM_PREVIEW}
            label="Ver todas las respuestas"
            onToggle={() => setAllReplies((value) => !value)}
          />
        )}
        {count === 0 && (
          <p className="text-ink-soft mt-1 text-sm">
            Sé parte de la conversación: deja la primera respuesta.
          </p>
        )}
        <div className="mt-5">
          <ChatComposer
            label="Tu respuesta"
            value={reply}
            onChange={setReply}
            placeholder="Escribe una respuesta…"
            busy={busy}
            onSubmit={sendReply}
          />
        </div>
        {confirmDialog}
      </div>
    );
  }

  // --- Thread list ---------------------------------------------------------
  return (
    <div>
      {notice && (
        <p
          role="status"
          className="border-ink bg-lemon border-chip mb-4 p-3 text-sm"
        >
          {notice}
        </p>
      )}
      <CommunityHeader
        title={meta?.title ?? "Foro del curso"}
        count={threads.length}
        description={meta?.description || undefined}
        action={
          !composing && (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className={`${btnPrimary} shrink-0 gap-2 px-4 py-2.5 text-sm`}
            >
              <Plus size={16} aria-hidden="true" /> Abrir un hilo
            </button>
          )
        }
      />
      {composing && (
        <div className="border-ink bg-bone border-chip rounded-control mt-5 p-4">
          <Composer
            label="Mensaje"
            value={body}
            onChange={setBody}
            rows={3}
            placeholder="Cuenta tu duda o comparte algo con la comunidad…"
            submitLabel="Publicar hilo"
            busy={busy}
            disabled={!title.trim()}
            onSubmit={createThread}
            note={
              <button
                type="button"
                onClick={() => setComposing(false)}
                className="hover:text-ink cursor-pointer underline underline-offset-2"
              >
                Cancelar
              </button>
            }
            before={
              <label className="mb-4 block">
                <span className="font-mono text-sm font-bold">Título</span>
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  autoFocus
                  maxLength={160}
                  placeholder="¿Sobre qué quieres hablar?"
                  className={cn(inputClass, "mt-1.5 px-3 py-2.5 text-base")}
                />
              </label>
            }
          />
        </div>
      )}

      {threads.length === 0 ? (
        <p className="text-ink-soft mt-6 flex items-center gap-1.5 text-sm">
          Todavía no hay hilos. Abre el primero
          <ArrowUp size={15} strokeWidth={2.5} aria-hidden="true" />
        </p>
      ) : (
        <ul className="border-ink bg-bone border-chip rounded-control divide-ink/15 mt-6 divide-y">
          {(allThreads ? threads : threads.slice(0, FORUM_PREVIEW)).map(
            (thread) => (
              <li
                key={thread.id}
                className={cn(
                  "flex items-center gap-2 pr-3",
                  !thread.approved && "bg-paper-dim/50",
                )}
              >
                <button
                  type="button"
                  onClick={() => openThread(thread.id)}
                  disabled={openLoading}
                  className={cn(
                    "hover:bg-paper group flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors",
                    !thread.approved && "opacity-70",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display truncate text-[15px] font-extrabold group-hover:underline">
                        {thread.title}
                      </h3>
                      {!thread.approved && <PendingBadge />}
                    </div>
                    <p className="text-ink-soft mt-0.5 truncate text-xs">
                      <span className="person-name">{thread.userName}</span> ·{" "}
                      {timeAgo(thread.createdAt)} · {thread.body}
                    </p>
                  </div>
                  <span className="text-ink-soft flex shrink-0 items-center gap-1 font-mono text-xs">
                    <MessageCircle size={14} aria-hidden="true" />
                    {thread.replyCount}
                    <ChevronRight
                      size={16}
                      aria-hidden="true"
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </span>
                </button>
                {canModerate && (
                  <RowMenu
                    label={`Moderar «${thread.title}»`}
                    items={
                      thread.approved
                        ? [
                            {
                              label: "Eliminar hilo",
                              icon: Trash2,
                              danger: true,
                              onSelect: () => confirmRemove(thread),
                            },
                          ]
                        : [
                            {
                              label: "Aprobar",
                              icon: Check,
                              onSelect: () => moderate(thread.id, true),
                            },
                            {
                              label: "Rechazar",
                              icon: X,
                              danger: true,
                              onSelect: () => moderate(thread.id, false),
                            },
                          ]
                    }
                  />
                )}
              </li>
            ),
          )}
        </ul>
      )}
      {threads.length > FORUM_PREVIEW && (
        <ShowAllButton
          expanded={allThreads}
          hidden={threads.length - FORUM_PREVIEW}
          label="Ver todos los hilos"
          onToggle={() => setAllThreads((value) => !value)}
        />
      )}
      {confirmDialog}
    </div>
  );
}
